import AddCars from "components/connectCars/addCars";
import { Select } from "components/shared/form";
import { featureModuleTypesEnum, organizationRegistrationStatusesEnum } from "helpers/enums";
import { paths } from "helpers/paths";
import { noRolesOtherThan } from "helpers/roles";
import React, { useCallback, useMemo } from "react";
import { Scrollbars } from "react-custom-scrollbars-2";
import { NavLink, Redirect, Route, Switch, useHistory, useLocation, useRouteMatch } from "react-router-dom";
import AccessControl from "./accessControl";
import Billing from "./billing";
import EducationResources from "./educationResources";
import EpdSettings from "./epdSettings";
import OrgEntityOptions from "./orgEntityOptions";
import OrgProfile from "./orgProfile";
import TaskSettings from "./taskSettings";
import UserProfile from "./userProfile";

const getTabs = (roles, profile, location, organization) => {
    const { availableFeatureModules } = profile;
    const {
        isCargoOwnerAdmin,
        isCargoOwnerManager,
        isTruckingOrganizationAdmin,
        isTruckingOrganizationManager,
        isTruckingOrganizationOperator,
        isTruckingOrganizationDispatcher,
        isTruckingOrganizationExtendedViewer,
        isIntegrationManager,
    } = roles;

    return [
        {
            title: "Оплата",
            path: "billing",
            Body: Billing,
            showInTabs: false,
            allow: location.pathname.startsWith(paths.profileBilling) && isTruckingOrganizationAdmin,
            editable: isTruckingOrganizationAdmin,
        },
        {
            title: "Профиль",
            path: "account",
            Body: UserProfile,
            showInTabs: false,
            allow:
                location.pathname === paths.profileAccount &&
                (isCargoOwnerAdmin ||
                    isTruckingOrganizationAdmin ||
                    isCargoOwnerManager ||
                    isTruckingOrganizationManager ||
                    isTruckingOrganizationOperator ||
                    isTruckingOrganizationDispatcher ||
                    isTruckingOrganizationExtendedViewer),
            editable:
                isCargoOwnerAdmin ||
                isTruckingOrganizationAdmin ||
                isCargoOwnerManager ||
                isTruckingOrganizationManager ||
                isTruckingOrganizationOperator ||
                isTruckingOrganizationDispatcher ||
                isTruckingOrganizationExtendedViewer,
        },
        {
            title: "Данные организации",
            path: "organization",
            Body: OrgProfile,
            showInTabs: true,
            allow:
                [
                    paths.profileOrgData,
                    paths.profileEpdSettings,
                    paths.profileEducationResources,
                    paths.profileAccess,
                    paths.profileEntityOptions,
                    paths.profileAccessMembers,
                    paths.profileAccessBlock,
                    paths.profileAccessInvited,
                    paths.profileConnectCars,
                    paths.profileTaskSettings,
                ].includes(location.pathname) &&
                !noRolesOtherThan(roles, [
                    "isTechSupport",
                    "isAccountManager",
                    "isDiagnostics",
                    "isIntegrationManager",
                ]),
            editable: isCargoOwnerAdmin || isTruckingOrganizationAdmin,
        },
        {
            title: "Настройки ЭПД",
            path: "epd-settings",
            Body: EpdSettings,
            showInTabs: true,
            allow:
                [
                    paths.profileOrgData,
                    paths.profileEpdSettings,
                    paths.profileEducationResources,
                    paths.profileAccess,
                    paths.profileEntityOptions,
                    paths.profileAccessMembers,
                    paths.profileAccessBlock,
                    paths.profileAccessInvited,
                    paths.profileConnectCars,
                    paths.profileTaskSettings,
                ].includes(location.pathname) &&
                isTruckingOrganizationAdmin &&
                availableFeatureModules?.includes(featureModuleTypesEnum.Waybills),
            editable: isTruckingOrganizationAdmin,
        },
        {
            title: "Управление доступом",
            path: "access",
            Body: AccessControl,
            showInTabs: true,
            allow:
                [
                    paths.profileOrgData,
                    paths.profileEpdSettings,
                    paths.profileEducationResources,
                    paths.profileAccess,
                    paths.profileEntityOptions,
                    paths.profileAccessMembers,
                    paths.profileAccessBlock,
                    paths.profileAccessInvited,
                    paths.profileConnectCars,
                    paths.profileTaskSettings,
                ].includes(location.pathname) &&
                (isCargoOwnerAdmin || isTruckingOrganizationAdmin || isIntegrationManager),
            editable: isCargoOwnerAdmin || isTruckingOrganizationAdmin || isIntegrationManager,
        },
        {
            title: "Настройка полей",
            path: "entityOptions",
            Body: OrgEntityOptions,
            showInTabs: true,
            allow: [
                paths.profileOrgData,
                paths.profileEpdSettings,
                paths.profileEducationResources,
                paths.profileAccess,
                paths.profileEntityOptions,
                paths.profileAccessMembers,
                paths.profileAccessBlock,
                paths.profileAccessInvited,
                paths.profileConnectCars,
                paths.profileTaskSettings,
            ].includes(location.pathname),
            editable: isCargoOwnerAdmin || isTruckingOrganizationAdmin,
        },
        {
            title: "Обучающие ресурсы водителей",
            path: "education-resources",
            Body: EducationResources,
            showInTabs: true,
            allow:
                [
                    paths.profileOrgData,
                    paths.profileEpdSettings,
                    paths.profileEducationResources,
                    paths.profileAccess,
                    paths.profileEntityOptions,
                    paths.profileAccessMembers,
                    paths.profileAccessBlock,
                    paths.profileAccessInvited,
                    paths.profileConnectCars,
                    paths.profileTaskSettings,
                ].includes(location.pathname) &&
                availableFeatureModules.includes(featureModuleTypesEnum.DriverEducationResources),
            editable: isCargoOwnerAdmin || isTruckingOrganizationAdmin,
        },
        {
            title: "Подключение машин",
            path: "connect-cars",
            Body: (props) => <AddCars embeddedMode="profile" {...props} />,
            showInTabs: true,
            allow:
                [
                    paths.profileOrgData,
                    paths.profileEpdSettings,
                    paths.profileEducationResources,
                    paths.profileAccess,
                    paths.profileEntityOptions,
                    paths.profileAccessMembers,
                    paths.profileAccessBlock,
                    paths.profileAccessInvited,
                    paths.profileConnectCars,
                    paths.profileTaskSettings,
                ].includes(location.pathname) &&
                isTruckingOrganizationAdmin &&
                organization.registrationStatus === organizationRegistrationStatusesEnum.Completed &&
                availableFeatureModules.includes(featureModuleTypesEnum.AccessToRelays),
            editable: isTruckingOrganizationAdmin,
        },
        {
            title: "Настройки задач",
            path: "task-settings",
            Body: TaskSettings,
            showInTabs: true,
            allow:
                [
                    paths.profileOrgData,
                    paths.profileEpdSettings,
                    paths.profileEducationResources,
                    paths.profileAccess,
                    paths.profileEntityOptions,
                    paths.profileAccessMembers,
                    paths.profileAccessBlock,
                    paths.profileAccessInvited,
                    paths.profileConnectCars,
                    paths.profileTaskSettings,
                ].includes(location.pathname) && isTruckingOrganizationAdmin,
            editable: isTruckingOrganizationAdmin,
        },
    ];
};

function ProfileBody(props) {
    const location = useLocation();
    const history = useHistory();
    const match = useRouteMatch();
    const { roles, profile, organization } = props;

    const tabs = useMemo(
        () => getTabs(roles, profile, location, organization),
        [roles, profile, location, organization],
    );

    const tabsList = useMemo(() => tabs.filter((item) => item.allow && item.showInTabs), [tabs]);

    const renderLinks = useCallback(
        (tabsList) =>
            tabsList.map((item) => (
                <li key={item.path}>
                    <NavLink
                        to={`${match.path}/${item.path}`}
                        className="profile-menu__link"
                        activeClassName="profile-menu__link--active"
                    >
                        {item.title}
                    </NavLink>
                </li>
            )),
        [match.path],
    );

    const renderMobileSelect = useCallback(() => {
        const availableTabs = tabsList;
        const setValue = ({ value }) => history.push(`${match.path}/${value}`);
        const pathnames = availableTabs.map((item) => item.path);
        const value = location.pathname.split("/").find((item) => pathnames.includes(item));
        return (
            <Select
                label="Выберите страницу"
                options={availableTabs.map(({ title, path }) => ({ label: title, value: path }))}
                inputProps={{ name: "type" }}
                value={value}
                onChange={setValue}
                unClearable
            />
        );
    }, [tabsList, history, match.path, location.pathname]);

    const renderRoutes = useCallback(() => {
        return tabs
            .filter((item) => item.allow)
            .map(({ Body, path, editable }) => (
                <Route
                    key={path}
                    path={`${match.path}/${path}`}
                    render={(routeProps) => <Body {...props} {...routeProps} editable={editable} />}
                />
            ));
    }, [tabs, match.path, props]);

    return (
        <div
            className={`profile__body profile-body ${location.pathname.startsWith(paths.profileBilling) ? "container-fluid" : "container-xl"}`}
        >
            {!!tabsList.length && (
                <Scrollbars style={{ height: 48 }}>
                    <ul className="profile__menu profile-menu no-bullet">{renderLinks(tabsList)}</ul>
                </Scrollbars>
            )}
            <div className="profile__nav-select">{renderMobileSelect()}</div>
            <Switch>
                <Route
                    exact
                    path={`${match.path}/`}
                    render={() => (
                        <Redirect
                            to={
                                noRolesOtherThan(roles, [
                                    "isTechSupport",
                                    "isAccountManager",
                                    "isDiagnostics",
                                    "isIntegrationManager",
                                ])
                                    ? paths.profileEntityOptions
                                    : paths.profileOrgData
                            }
                        />
                    )}
                />
                {renderRoutes()}
            </Switch>
        </div>
    );
}

export default ProfileBody;
export { getTabs as tabs };
